import { useEffect, useRef, useState } from "react";
import api, { API } from "@/lib/api";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { ArrowLeft, Save, Download, Sparkles, Loader2, Plus, Trash2, Wand2, Eye, EyeOff, Bot, Check, Undo2, Redo2, ExternalLink, AlertTriangle, FileCheck2, ArrowUp, ArrowDown, CornerDownRight, MessageSquareQuote, Target, Send, User as User2, Briefcase as Briefcase2, GraduationCap, Award as Award2, Layers as LayoutPanel, Scissors as ScissorsIcon, FileSearch, GripVertical, Lightbulb } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import PreviewCanvas from "@/components/PreviewCanvas";
import JobScoreDrawer from "@/components/JobScoreDrawer";
import SendEmailDialog from "@/components/SendEmailDialog";
import AIAssistantDrawer from "@/components/AIAssistantDrawer";
import AIAutoFitDialog from "@/components/AIAutoFitDialog";
import SkillGapDrawer from "@/components/SkillGapDrawer";
import { toast } from "sonner";

const SECTIONS = [
  { key: "summary", label: "Summary" },
  { key: "experiences", label: "Experience" },
  { key: "earlier_roles", label: "Earlier roles" },
  { key: "education", label: "Education" },
  { key: "projects", label: "Projects" },
  { key: "awards", label: "Awards" },
  { key: "publications", label: "Publications" },
  { key: "volunteer", label: "Volunteer" },
  { key: "competencies", label: "Competencies" },
  { key: "certifications", label: "Certifications" },
  { key: "languages", label: "Languages" },
  { key: "highlights", label: "Highlights" },
  { key: "additional_info", label: "Additional info" },
];

const empty = {
  name: "", title: "", summary: "", photo_url: "",
  contact: { email: "", phone: "", location: "", linkedin: "", website: "" },
  experiences: [], earlier_roles: [], education: [], competencies: [], certifications: [], languages: [], highlights: [],
  projects: [], awards: [], publications: [], volunteer: [],
  additional_info: [],
  visibility: {},
};

export default function Editor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [resume, setResume] = useState(null);
  const [data, setData] = useState(empty);
  const [templates, setTemplates] = useState([]);
  const [clients, setClients] = useState([]);
  const [aiBusy, setAiBusy] = useState(false);
  const [exporting, setExporting] = useState("");
  const [chatOpen, setChatOpen] = useState(false);
  const [autofitOpen, setAutofitOpen] = useState(false);
  const [skillGapOpen, setSkillGapOpen] = useState(false);
  const [saveState, setSaveState] = useState("idle"); // idle | dirty | saving | saved
  const [metrics, setMetrics] = useState({ heightMm: 0, pages: 1, pageHeightMm: 297, pageWidthMm: 210 });
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const photoRef = useRef(null);
  const [scoreOpen, setScoreOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const pipelineStatus = resume?.pipeline_status || "draft";

  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post(`/resumes/${id}/upload-photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResume(r.data);
      setData(r.data.data);
      toast.success("Photo uploaded");
    } catch { toast.error("Upload failed"); } finally { setUploadingPhoto(false); if (photoRef.current) photoRef.current.value = ""; }
  };

  const setPipelineStatus = async (next) => {
    try {
      const r = await api.patch(`/resumes/${id}/status`, { pipeline_status: next });
      setResume(r.data);
      toast.success(`Marked as ${next.toUpperCase()}`);
    } catch { toast.error("Status update failed"); }
  };

  const fixVoice = async () => {
    if (!window.confirm("Rewrite this resume in proper resume voice?\n\nThis will convert biographical third-person tone (e.g. 'X is a motivated engineer...') into professional implied first-person resume voice (e.g. 'Motivated engineer with 3+ years...').\n\nFacts, dates, companies and numbers are preserved.")) return;
    setAiBusy(true);
    try {
      const r = await api.post(`/resumes/${id}/fix-voice`);
      setResume(r.data);
      setData(r.data.data);
      toast.success("Resume voice fixed — now reads like your own resume");
    } catch (e) { toast.error(e?.response?.data?.detail || "Voice fix failed"); } finally { setAiBusy(false); }
  };

  const dirtyRef = useRef(false);
  const saveTimer = useRef(null);

  // Undo/Redo history (capped at 50 entries)
  const historyRef = useRef({ past: [], future: [] });
  const lastSnapshotRef = useRef("");
  const undoingRef = useRef(false);

  useEffect(() => {
    api.get(`/resumes/${id}`).then((r) => { setResume(r.data); setData(r.data.data); lastSnapshotRef.current = JSON.stringify(r.data.data); });
    api.get("/templates").then((r) => setTemplates(r.data));
    api.get("/clients").then((r) => setClients(r.data || [])).catch(() => {});
  }, [id]);

  const currentTemplate = templates.find((t) => t.id === resume?.template_id) || templates.find((t) => t.is_default) || null;

  // Mark dirty whenever data changes (after initial load) + push to history
  useEffect(() => {
    if (!resume) return;
    if (!dirtyRef.current) { dirtyRef.current = true; return; }

    // Push previous snapshot into past, debounced (avoid every keystroke)
    if (!undoingRef.current) {
      const snap = JSON.stringify(data);
      if (snap !== lastSnapshotRef.current) {
        const past = [...historyRef.current.past, lastSnapshotRef.current].slice(-50);
        historyRef.current = { past, future: [] };
        lastSnapshotRef.current = snap;
      }
    } else {
      undoingRef.current = false;
    }

    setSaveState("dirty");
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => { autoSave(); }, 1200);
    return () => { if (saveTimer.current) clearTimeout(saveTimer.current); };
    // eslint-disable-next-line
  }, [data]);

  const undo = () => {
    const { past, future } = historyRef.current;
    if (!past.length) return;
    const prev = past[past.length - 1];
    const newFuture = [lastSnapshotRef.current, ...future].slice(0, 50);
    historyRef.current = { past: past.slice(0, -1), future: newFuture };
    lastSnapshotRef.current = prev;
    undoingRef.current = true;
    setData(JSON.parse(prev));
  };

  const redo = () => {
    const { past, future } = historyRef.current;
    if (!future.length) return;
    const next = future[0];
    const newPast = [...past, lastSnapshotRef.current].slice(-50);
    historyRef.current = { past: newPast, future: future.slice(1) };
    lastSnapshotRef.current = next;
    undoingRef.current = true;
    setData(JSON.parse(next));
  };

  // Keyboard shortcuts: Cmd/Ctrl+Z, Cmd/Ctrl+Shift+Z (or Ctrl+Y)
  useEffect(() => {
    const onKey = (e) => {
      const mod = e.metaKey || e.ctrlKey;
      if (mod && e.key.toLowerCase() === "z" && !e.shiftKey) {
        const tag = (document.activeElement?.tagName || "").toLowerCase();
        // Allow native undo in text fields
        if (tag === "input" || tag === "textarea") return;
        e.preventDefault(); undo();
      } else if ((mod && e.shiftKey && e.key.toLowerCase() === "z") || (mod && e.key.toLowerCase() === "y")) {
        const tag = (document.activeElement?.tagName || "").toLowerCase();
        if (tag === "input" || tag === "textarea") return;
        e.preventDefault(); redo();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  const autoSave = async () => {
    setSaveState("saving");
    try {
      await api.put(`/resumes/${id}`, { data });
      setSaveState("saved");
      setTimeout(() => setSaveState((s) => (s === "saved" ? "idle" : s)), 1500);
    } catch { setSaveState("dirty"); toast.error("Auto-save failed"); }
  };

  const update = (patch) => setData((d) => ({ ...d, ...patch }));
  const updateContact = (patch) => setData((d) => ({ ...d, contact: { ...d.contact, ...patch } }));
  const toggleSection = (key) => {
    setData((d) => {
      const v = { ...(d.visibility || {}) };
      const currentlyVisible = v[key] !== false;
      v[key] = !currentlyVisible;
      return { ...d, visibility: v };
    });
  };

  const changeTemplate = async (tid) => {
    setResume((r) => ({ ...r, template_id: tid }));
    try { await api.put(`/resumes/${id}`, { template_id: tid }); toast.success("Template applied"); } catch { toast.error("Failed to switch template"); }
  };

  const changeClient = async (cid) => {
    const chosen = clients.find((c) => c.id === cid);
    const effectiveId = cid === "__none__" ? null : cid;
    setResume((r) => ({ ...r, client_id: effectiveId, client_name: chosen?.name || "" }));
    try { await api.put(`/resumes/${id}`, { client_id: effectiveId, client_name: chosen?.name || "" }); toast.success(chosen ? `Linked to ${chosen.name}` : "Client unlinked"); } catch { toast.error("Failed to set client"); }
  };

  const save = async () => {
    await autoSave();
  };

  const exportFile = async (kind) => {
    setExporting(kind);
    try {
      await autoSave();
      const token = localStorage.getItem("rf_token");
      const res = await fetch(`${API}/resumes/${id}/export/${kind}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `${(data.name || "resume").replace(/\s+/g, "_")}.${kind}`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      toast.success(`${kind.toUpperCase()} downloaded`);
    } catch { toast.error("Export failed"); } finally { setExporting(""); }
  };

  const aiRewrite = async (section, content, applyFn) => {
    setAiBusy(true);
    try {
      const r = await api.post(`/resumes/${id}/ai-rewrite`, { section, content, context: data });
      applyFn(r.data.result);
      toast.success("AI suggestion applied");
    } catch (e) { toast.error("AI failed"); } finally { setAiBusy(false); }
  };

  const onChatPatch = (patch) => {
    setData((d) => ({ ...patch, visibility: d.visibility || {} }));
  };

  // 1-click AI Auto-Fit to exactly N pages — sends current page count so
  // the backend knows the exact overflow delta and rewrites accordingly.
  const onAiFitToPages = async (n) => {
    setAiBusy(true);
    try {
      const cur = metrics?.pages || 0;
      const res = await api.post(`/resumes/${id}/fit-to-pages?target_pages=${n}&current_pages=${cur}`);
      if (res.data?.data) {
        setData(res.data.data);
        toast.success(`AI fit to ${n} page${n > 1 ? "s" : ""} — review the preview.`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI fit failed");
    } finally {
      setAiBusy(false);
    }
  };

  // Inline content-editable in preview — receives a dotted path like
  //   "name", "summary", "experiences.0.bullets.3", "experiences.1.company"
  // and writes the new text back to the resume data immutably.
  const onInlineTextEdit = (path, newValue) => {
    if (!path) return;
    const parts = path.split(".");
    setData((prev) => {
      // deep clone path
      const root = Array.isArray(prev) ? [...prev] : { ...prev };
      let cur = root;
      for (let i = 0; i < parts.length - 1; i++) {
        const k = parts[i];
        const idx = /^\d+$/.test(k) ? Number(k) : k;
        const next = cur[idx];
        const clone = Array.isArray(next) ? [...next] : { ...(next || {}) };
        cur[idx] = clone;
        cur = clone;
      }
      const last = parts[parts.length - 1];
      const lastKey = /^\d+$/.test(last) ? Number(last) : last;
      // Special handling: "experiences.{i}.company" maps the combined string
      // "Company · Location" back into the two fields.
      if (parts[0] === "experiences" && last === "company") {
        const segs = String(newValue || "").split(/\s+·\s+|\s+•\s+/);
        cur.company = segs[0] || "";
        if (segs.length > 1) cur.location = segs.slice(1).join(" · ");
      } else {
        cur[lastKey] = newValue;
      }
      return root;
    });
  };

  const onAutofitApply = (newData) => {
    setData((d) => ({ ...newData, visibility: d.visibility || {} }));
  };

  const openFullPreview = async () => {
    // Make sure latest changes are persisted so /print fetches fresh data
    try { await autoSave(); } catch { /* non-fatal */ }
    const token = localStorage.getItem("rf_token");
    const url = `${window.location.origin}/print/${id}?token=${encodeURIComponent(token || "")}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  if (!resume) return <div className="p-10 text-muted-foreground">Loading…</div>;

  return (
    <div className="flex flex-col h-screen">
      {/* Toolbar */}
      <div className="border-b border-border bg-card flex items-center justify-between px-4 md:px-6 py-3">
        <div className="flex items-center gap-3 min-w-0">
          <Button variant="ghost" size="icon" onClick={() => navigate("/organizer")} data-testid="editor-back-button"><ArrowLeft className="size-4" /></Button>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold flex items-center gap-2">
              Editing
              <SaveBadge state={saveState} />
            </div>
            <Input value={resume.title} onChange={(e) => setResume({ ...resume, title: e.target.value })} onBlur={() => api.put(`/resumes/${id}`, { title: resume.title })} className="h-8 border-0 px-0 font-bold display text-lg focus-visible:ring-0" data-testid="editor-title-input" />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={undo} title="Undo (Ctrl/Cmd+Z)" data-testid="editor-undo-button"><Undo2 className="size-4" /></Button>
          <Button variant="ghost" size="icon" onClick={redo} title="Redo (Ctrl/Cmd+Shift+Z)" data-testid="editor-redo-button"><Redo2 className="size-4" /></Button>
          <Select value={pipelineStatus} onValueChange={setPipelineStatus}>
            <SelectTrigger className="w-[148px] h-9 text-[11px] font-bold uppercase tracking-[0.12em]" data-testid="editor-status-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="draft">📝 Draft</SelectItem>
              <SelectItem value="ready">✨ Ready</SelectItem>
              <SelectItem value="submitted">📬 Submitted</SelectItem>
              <SelectItem value="interview">🎯 Interview</SelectItem>
              <SelectItem value="offer">🎁 Offer</SelectItem>
              <SelectItem value="placed">🏆 Placed</SelectItem>
              <SelectItem value="hold">⏸️ On Hold</SelectItem>
              <SelectItem value="rejected">✖ Rejected</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={openFullPreview} className="gap-2 lift" title="Open the exact print render in a new tab" data-testid="editor-fullpreview-button"><ExternalLink className="size-4" /> Full preview</Button>
          <Button variant="outline" onClick={() => setScoreOpen(true)} className="gap-2 lift" data-testid="editor-score-button"><Target className="size-4" /> Job Score</Button>
          <Button variant="outline" onClick={() => setEmailOpen(true)} className="gap-2 lift" data-testid="editor-send-email-button"><Send className="size-4" /> Send</Button>
          <Button variant="outline" onClick={fixVoice} disabled={aiBusy} className="gap-2 lift" title="Rewrite in proper resume voice (no biographical 'X is a...' tone)" data-testid="editor-fix-voice-button">
            {aiBusy ? <Loader2 className="size-4 animate-spin" /> : <MessageSquareQuote className="size-4" />} Fix voice
          </Button>
          <Button variant="outline" onClick={() => setAutofitOpen(true)} className="gap-2 lift" data-testid="editor-autofit-button"><Wand2 className="size-4" /> Auto-Fit</Button>
          <Button variant="outline" onClick={() => setSkillGapOpen(true)} className="gap-2 lift" data-testid="editor-skillgap-button"><Lightbulb className="size-4" /> Skill-gap</Button>
          <Button variant="outline" onClick={() => setChatOpen(true)} className="gap-2 lift" data-testid="editor-chat-button"><Bot className="size-4" /> AI Copilot</Button>
          <Select value={resume?.template_id || ""} onValueChange={changeTemplate}>
            <SelectTrigger className="w-[170px] h-9" data-testid="editor-template-select">
              <SelectValue placeholder="Template" />
            </SelectTrigger>
            <SelectContent>
              {templates.map((t) => (<SelectItem key={t.id} value={t.id}>{t.name}{t.is_default ? " · default" : ""}</SelectItem>))}
            </SelectContent>
          </Select>
          {clients.length > 0 && (
            <Select value={resume?.client_id || "__none__"} onValueChange={changeClient}>
              <SelectTrigger className="w-[160px] h-9" data-testid="editor-client-select">
                <SelectValue placeholder="Client" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— No client —</SelectItem>
                {clients.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
              </SelectContent>
            </Select>
          )}
          <Button variant="outline" onClick={() => exportFile("docx")} disabled={!!exporting} className="gap-2 lift" data-testid="export-docx-button">
            {exporting === "docx" ? <Loader2 className="size-4 animate-spin" /> : <Download className="size-4" />} DOCX
          </Button>
          <Button onClick={() => exportFile("pdf")} disabled={!!exporting} className="gap-2 lift" data-testid="export-pdf-button">
            {exporting === "pdf" ? <Loader2 className="size-4 animate-spin" /> : <Download className="size-4" />} PDF
          </Button>
        </div>
      </div>

      {/* Split */}
      <div className="grid grid-cols-1 lg:grid-cols-2 flex-1 min-h-0 overflow-hidden">
        {/* Left: editor */}
        <div className="overflow-y-auto p-5 md:p-7 border-r border-border bg-background">
          {/* Section visibility toggle bar */}
          <Card className="border-border mb-4">
            <CardContent className="p-3">
              <div className="flex items-center gap-2 mb-2">
                <Eye className="size-3.5 text-muted-foreground" />
                <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">Section visibility</div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {SECTIONS.map((sec) => {
                  const visible = (data.visibility?.[sec.key] !== false);
                  return (
                    <button key={sec.key} onClick={() => toggleSection(sec.key)} data-testid={`visibility-toggle-${sec.key}`}
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border lift ${visible ? "bg-accent text-accent-foreground border-primary/30" : "bg-secondary text-muted-foreground border-border line-through"}`}>
                      {visible ? <Eye className="size-3" /> : <EyeOff className="size-3" />} {sec.label}
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          <Tabs defaultValue="basics">
            <TabsList className="grid grid-cols-7 w-full bg-secondary/40 p-1 h-auto rounded-xl gap-0.5" data-testid="editor-tabs">
              {[
                { v: "basics",     icon: User2,         label: "Basics" },
                { v: "experience", icon: Briefcase2,    label: "Experience" },
                { v: "education",  icon: GraduationCap, label: "Education" },
                { v: "extras",     icon: Award2,        label: "Extras" },
                { v: "sidebar",    icon: LayoutPanel,   label: "Sidebar" },
                { v: "custom",     icon: Plus,          label: "Custom" },
                { v: "breaks",     icon: ScissorsIcon,  label: "Page" },
              ].map((t) => (
                <TabsTrigger key={t.v} value={t.v} data-testid={`tab-${t.v}`} className="flex flex-col items-center justify-center gap-1 py-2 px-1 rounded-lg text-[10px] uppercase tracking-[0.12em] font-bold data-[state=active]:bg-card data-[state=active]:shadow-md data-[state=active]:text-primary transition-all">
                  <t.icon className="size-4" />
                  <span className="leading-none">{t.label}</span>
                </TabsTrigger>
              ))}
            </TabsList>

            {/* Basics */}
            <TabsContent value="basics" className="mt-5 space-y-4">
              <Card className="border-border">
                <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    <div className="shrink-0">
                      <Label className="text-xs font-semibold">Candidate photo</Label>
                      <div className="mt-1.5 relative">
                        <div className="size-20 rounded-full border-2 border-border overflow-hidden bg-muted grid place-items-center">
                          {data.photo_url
                            ? <img src={`${API.replace(/\/api$/, "")}${data.photo_url}`} alt="photo" className="w-full h-full object-cover" />
                            : <Sparkles className="size-6 text-muted-foreground" />}
                        </div>
                        <input type="file" accept=".png,.jpg,.jpeg,.webp" ref={photoRef} onChange={onPhoto} className="hidden" data-testid="candidate-photo-input" />
                        <button onClick={() => photoRef.current?.click()} disabled={uploadingPhoto} className="absolute -bottom-1 -right-1 size-7 grid place-items-center rounded-full bg-primary text-primary-foreground border-2 border-card lift" data-testid="candidate-photo-upload" title="Upload candidate photo">
                          {uploadingPhoto ? <Loader2 className="size-3.5 animate-spin" /> : <Plus className="size-3.5" />}
                        </button>
                      </div>
                      {data.photo_url && (
                        <button onClick={() => update({ photo_url: "" })} className="mt-2 text-[10px] uppercase tracking-wider text-destructive hover:underline font-semibold" data-testid="candidate-photo-remove">Remove</button>
                      )}
                    </div>
                    <div className="flex-1 grid grid-cols-2 gap-3">
                      <Field label="Full name" testid="field-name"><Input value={data.name} onChange={(e) => update({ name: e.target.value })} /></Field>
                      <Field label="Title / Role" testid="field-title"><Input value={data.title} onChange={(e) => update({ title: e.target.value })} /></Field>
                      <Field label="Email" testid="field-email"><Input value={data.contact.email} onChange={(e) => updateContact({ email: e.target.value })} /></Field>
                      <Field label="Phone" testid="field-phone"><Input value={data.contact.phone} onChange={(e) => updateContact({ phone: e.target.value })} /></Field>
                      <Field label="Location" testid="field-location"><Input value={data.contact.location} onChange={(e) => updateContact({ location: e.target.value })} /></Field>
                      <Field label="LinkedIn" testid="field-linkedin"><Input value={data.contact.linkedin} onChange={(e) => updateContact({ linkedin: e.target.value })} /></Field>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-border">
                <CardContent className="p-4">
                  <SectionHeader title="Professional Summary" onAi={() => aiRewrite("summary", data.summary, (out) => update({ summary: out }))} aiBusy={aiBusy} testidPrefix="summary" />
                  <Textarea rows={5} value={data.summary} onChange={(e) => update({ summary: e.target.value })} data-testid="field-summary" />
                </CardContent>
              </Card>

              <Card className="border-border">
                <CardContent className="p-4 space-y-3">
                  <div className="display font-bold">Candidate metadata</div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                    <div>
                      <Label className="text-xs font-semibold">Experience level</Label>
                      <Select value={resume?.experience_level || "__none__"} onValueChange={async (v) => {
                        const eff = v === "__none__" ? null : v;
                        setResume((r) => ({ ...r, experience_level: eff }));
                        try { await api.put(`/resumes/${id}`, { experience_level: eff }); toast.success("Level updated"); } catch { toast.error("Save failed"); }
                      }}>
                        <SelectTrigger className="mt-1" data-testid="editor-experience-level"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">— Unspecified —</SelectItem>
                          <SelectItem value="fresher">Fresher (0-1 yr)</SelectItem>
                          <SelectItem value="junior">Junior (1-3 yrs)</SelectItem>
                          <SelectItem value="mid">Mid-level (3-6 yrs)</SelectItem>
                          <SelectItem value="senior">Senior (6-10 yrs)</SelectItem>
                          <SelectItem value="lead">Lead (10-15 yrs)</SelectItem>
                          <SelectItem value="executive">Executive (15+ yrs)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs font-semibold">HR feedback status</Label>
                      <Select value={resume?.hr_feedback_status || "pending"} onValueChange={async (v) => {
                        setResume((r) => ({ ...r, hr_feedback_status: v }));
                        try { await api.put(`/resumes/${id}`, { hr_feedback_status: v }); toast.success("HR status updated"); } catch { toast.error("Save failed"); }
                      }}>
                        <SelectTrigger className="mt-1" data-testid="editor-hr-status"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="shortlisted">Shortlisted</SelectItem>
                          <SelectItem value="rejected">Rejected</SelectItem>
                          <SelectItem value="hold">Hold</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs font-semibold">Interviewer status</Label>
                      <Select value={resume?.interviewer_feedback_status || "pending"} onValueChange={async (v) => {
                        setResume((r) => ({ ...r, interviewer_feedback_status: v }));
                        try { await api.put(`/resumes/${id}`, { interviewer_feedback_status: v }); toast.success("Interviewer status updated"); } catch { toast.error("Save failed"); }
                      }}>
                        <SelectTrigger className="mt-1" data-testid="editor-interviewer-status"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="selected">Selected</SelectItem>
                          <SelectItem value="rejected">Rejected</SelectItem>
                          <SelectItem value="hold">Hold</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs font-semibold">HR feedback (notes)</Label>
                      <Textarea rows={3} value={resume?.hr_feedback || ""} onChange={(e) => setResume((r) => ({ ...r, hr_feedback: e.target.value }))} onBlur={async (e) => { try { await api.put(`/resumes/${id}`, { hr_feedback: e.target.value }); } catch { /* ignore */ } }} placeholder="HR notes after screening…" data-testid="editor-hr-feedback" />
                    </div>
                    <div>
                      <Label className="text-xs font-semibold">Interviewer feedback (notes)</Label>
                      <Textarea rows={3} value={resume?.interviewer_feedback || ""} onChange={(e) => setResume((r) => ({ ...r, interviewer_feedback: e.target.value }))} onBlur={async (e) => { try { await api.put(`/resumes/${id}`, { interviewer_feedback: e.target.value }); } catch { /* ignore */ } }} placeholder="Combined interviewer notes…" data-testid="editor-interviewer-feedback" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Experience */}
            <TabsContent value="experience" className="mt-5 space-y-4">
              <Card className="border-border">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="display font-bold">Professional Experience</div>
                    <Button size="sm" variant="outline" className="gap-1" onClick={() => update({ experiences: [...data.experiences, { role: "", company: "", location: "", start_date: "", end_date: "", bullets: [] }] })} data-testid="exp-add">
                      <Plus className="size-3.5" /> Add role
                    </Button>
                  </div>
                  <div className="space-y-4">
                    {data.experiences.map((e, idx) => (
                      <div key={idx} className="border border-border rounded-md p-3 transition-all" data-testid={`exp-item-${idx}`} {...reorderProps("experiences", idx)}>
                        <div className="flex items-center justify-between mb-2 -mt-1">
                          <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-muted-foreground flex items-center gap-1.5">
                            <GripVertical className="size-3.5 text-muted-foreground/60 cursor-grab" /> Role {idx + 1}
                          </div>
                          <div className="flex items-center gap-1">
                            <Button size="icon" variant="ghost" disabled={idx === 0} onClick={() => moveExp(idx, -1)} title="Move up" data-testid={`exp-up-${idx}`}><ArrowUp className="size-4" /></Button>
                            <Button size="icon" variant="ghost" disabled={idx === data.experiences.length - 1} onClick={() => moveExp(idx, 1)} title="Move down" data-testid={`exp-down-${idx}`}><ArrowDown className="size-4" /></Button>
                            <Button size="sm" variant={e.page_break_after ? "default" : "outline"} onClick={() => updateExp(idx, { page_break_after: !e.page_break_after })} title="Force a new page after this role" className="gap-1 h-8 px-2" data-testid={`exp-break-${idx}`}>
                              <CornerDownRight className="size-3.5" /> {e.page_break_after ? "Breaks after" : "Break after"}
                            </Button>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <Input placeholder="Role" value={e.role} onChange={(ev) => updateExp(idx, { role: ev.target.value })} />
                          <Input placeholder="Company" value={e.company} onChange={(ev) => updateExp(idx, { company: ev.target.value })} />
                          <Input placeholder="Location" value={e.location} onChange={(ev) => updateExp(idx, { location: ev.target.value })} />
                          <div className="grid grid-cols-2 gap-2">
                            <Input placeholder="Start" value={e.start_date} onChange={(ev) => updateExp(idx, { start_date: ev.target.value })} />
                            <Input placeholder="End" value={e.end_date} onChange={(ev) => updateExp(idx, { end_date: ev.target.value })} />
                          </div>
                        </div>
                        <div className="mt-3">
                          <div className="flex items-center justify-between mb-1.5">
                            <Label className="text-xs font-semibold">Impact bullets</Label>
                            <div className="flex gap-1.5">
                              <Button size="sm" variant="outline" onClick={() => aiRewrite("experience_bullets", JSON.stringify(e.bullets), (out) => {
                                try { const arr = JSON.parse(out); if (Array.isArray(arr)) updateExp(idx, { bullets: arr }); }
                                catch { updateExp(idx, { bullets: out.split("\n").map(s => s.replace(/^[-•\s]+/, "").trim()).filter(Boolean) }); }
                              })} disabled={aiBusy} className="gap-1" data-testid={`exp-ai-${idx}`}>
                                <Wand2 className="size-3.5" /> AI rewrite
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => updateExp(idx, { bullets: [...e.bullets, ""] })} className="gap-1"><Plus className="size-3.5" /></Button>
                            </div>
                          </div>
                          {e.bullets.map((b, j) => {
                            const bk = `exp-${idx}-${j}`;
                            const hasBreak = !!(data.bullet_breaks || {})[bk];
                            return (
                              <div key={j} className="flex gap-2 mb-1.5 items-center">
                                <Input value={b} onChange={(ev) => { const bb = [...e.bullets]; bb[j] = ev.target.value; updateExp(idx, { bullets: bb }); }} />
                                <Button size="icon" variant={hasBreak ? "default" : "ghost"} onClick={() => update({ bullet_breaks: { ...(data.bullet_breaks || {}), [bk]: !hasBreak } })} title="Force page break after this bullet" className="shrink-0" data-testid={`exp-bullet-break-${idx}-${j}`}>
                                  <CornerDownRight className="size-4" />
                                </Button>
                                <Button size="icon" variant="ghost" onClick={() => updateExp(idx, { bullets: e.bullets.filter((_, jj) => jj !== j) })}><Trash2 className="size-4 text-destructive" /></Button>
                              </div>
                            );
                          })}
                        </div>
                        <div className="flex justify-end mt-2">
                          <Button size="sm" variant="ghost" className="text-destructive" onClick={() => update({ experiences: data.experiences.filter((_, i) => i !== idx) })} data-testid={`exp-remove-${idx}`}>Remove role</Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card className="border-border">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="display font-bold">Earlier Roles</div>
                    <Button size="sm" variant="outline" className="gap-1" onClick={() => update({ earlier_roles: [...data.earlier_roles, { role: "", company: "", duration: "" }] })} data-testid="earlier-add"><Plus className="size-3.5" /> Add</Button>
                  </div>
                  {data.earlier_roles.map((r, i) => (
                    <div key={i} className="flex gap-2 mb-2" data-testid={`earlier-item-${i}`}>
                      <Input className="flex-[3]" placeholder="Role" value={r.role} onChange={(e) => { const arr = [...data.earlier_roles]; arr[i] = { ...arr[i], role: e.target.value }; update({ earlier_roles: arr }); }} />
                      <Input className="flex-[3]" placeholder="Company" value={r.company} onChange={(e) => { const arr = [...data.earlier_roles]; arr[i] = { ...arr[i], company: e.target.value }; update({ earlier_roles: arr }); }} />
                      <Input className="flex-[2]" placeholder="Duration" value={r.duration} onChange={(e) => { const arr = [...data.earlier_roles]; arr[i] = { ...arr[i], duration: e.target.value }; update({ earlier_roles: arr }); }} />
                      <Button size="icon" variant={r.page_break_after ? "default" : "ghost"} onClick={() => { const arr = [...data.earlier_roles]; arr[i] = { ...arr[i], page_break_after: !arr[i].page_break_after }; update({ earlier_roles: arr }); }} title="Force page break after this role" data-testid={`er-break-${i}`}><CornerDownRight className="size-4" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => update({ earlier_roles: data.earlier_roles.filter((_, j) => j !== i) })}><Trash2 className="size-4 text-destructive" /></Button>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Education */}
            <TabsContent value="education" className="mt-5 space-y-4">
              <Card className="border-border">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="display font-bold">Education</div>
                    <Button size="sm" variant="outline" onClick={() => update({ education: [...data.education, { degree: "", institution: "", location: "", duration: "", details: "" }] })} className="gap-1" data-testid="edu-add"><Plus className="size-3.5" /> Add</Button>
                  </div>
                  {data.education.map((ed, i) => (
                    <div key={i} className="border border-border rounded-md p-3 mb-3 grid grid-cols-2 gap-2 transition-all" data-testid={`edu-item-${i}`} {...reorderProps("education", i)}>
                      <div className="col-span-2 flex items-center justify-between -mt-1 mb-1">
                        <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-muted-foreground flex items-center gap-1.5">
                          <GripVertical className="size-3.5 text-muted-foreground/60 cursor-grab" /> Education {i + 1}
                        </div>
                        <div className="flex items-center gap-1">
                          <Button size="icon" variant="ghost" disabled={i === 0} onClick={() => moveItem("education", i, -1)} title="Move up" data-testid={`edu-up-${i}`}><ArrowUp className="size-4" /></Button>
                          <Button size="icon" variant="ghost" disabled={i === data.education.length - 1} onClick={() => moveItem("education", i, 1)} title="Move down" data-testid={`edu-down-${i}`}><ArrowDown className="size-4" /></Button>
                          <Button size="sm" variant={ed.page_break_after ? "default" : "outline"} onClick={() => updEdu(i, { page_break_after: !ed.page_break_after })} title="Force a new page after this" className="gap-1 h-8 px-2" data-testid={`edu-break-${i}`}>
                            <CornerDownRight className="size-3.5" /> {ed.page_break_after ? "Breaks" : "Break"}
                          </Button>
                        </div>
                      </div>
                      <Input placeholder="Degree" value={ed.degree} onChange={(e) => updEdu(i, { degree: e.target.value })} />
                      <Input placeholder="Institution" value={ed.institution} onChange={(e) => updEdu(i, { institution: e.target.value })} />
                      <Input placeholder="Location" value={ed.location} onChange={(e) => updEdu(i, { location: e.target.value })} />
                      <Input placeholder="Duration" value={ed.duration} onChange={(e) => updEdu(i, { duration: e.target.value })} />
                      <Textarea className="col-span-2" placeholder="Details" value={ed.details} onChange={(e) => updEdu(i, { details: e.target.value })} />
                      <div className="col-span-2 flex justify-end"><Button size="sm" variant="ghost" className="text-destructive" onClick={() => update({ education: data.education.filter((_, j) => j !== i) })}>Remove</Button></div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Extras (projects / awards / publications / volunteer) */}
            <TabsContent value="extras" className="mt-5 space-y-4">
              <Card className="border-border">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="display font-bold">Projects</div>
                    <Button size="sm" variant="outline" className="gap-1" onClick={() => update({ projects: [...(data.projects || []), { name: "", role: "", tech: "", duration: "", link: "", description: "", bullets: [] }] })} data-testid="proj-add"><Plus className="size-3.5" /> Add project</Button>
                  </div>
                  {(data.projects || []).map((p, i) => (
                    <div key={i} className="border border-border rounded-md p-3 mb-3 transition-all" data-testid={`proj-item-${i}`} {...reorderProps("projects", i)}>
                      <div className="flex items-center justify-between mb-2 -mt-1">
                        <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-muted-foreground flex items-center gap-1.5">
                          <GripVertical className="size-3.5 text-muted-foreground/60 cursor-grab" /> Project {i + 1}
                        </div>
                        <div className="flex items-center gap-1">
                          <Button size="icon" variant="ghost" disabled={i === 0} onClick={() => moveItem("projects", i, -1)} data-testid={`proj-up-${i}`}><ArrowUp className="size-4" /></Button>
                          <Button size="icon" variant="ghost" disabled={i === data.projects.length - 1} onClick={() => moveItem("projects", i, 1)} data-testid={`proj-down-${i}`}><ArrowDown className="size-4" /></Button>
                          <Button size="sm" variant={p.page_break_after ? "default" : "outline"} onClick={() => { const arr=[...data.projects]; arr[i]={...arr[i], page_break_after: !arr[i].page_break_after}; update({projects: arr}); }} className="gap-1 h-8 px-2" data-testid={`proj-break-${i}`}>
                            <CornerDownRight className="size-3.5" /> {p.page_break_after ? "Breaks" : "Break"}
                          </Button>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <Input placeholder="Project name" value={p.name} onChange={(e) => { const arr = [...data.projects]; arr[i] = { ...arr[i], name: e.target.value }; update({ projects: arr }); }} />
                        <Input placeholder="Role" value={p.role} onChange={(e) => { const arr = [...data.projects]; arr[i] = { ...arr[i], role: e.target.value }; update({ projects: arr }); }} />
                        <Input placeholder="Tech stack (comma-sep)" value={p.tech} onChange={(e) => { const arr = [...data.projects]; arr[i] = { ...arr[i], tech: e.target.value }; update({ projects: arr }); }} />
                        <Input placeholder="Duration" value={p.duration} onChange={(e) => { const arr = [...data.projects]; arr[i] = { ...arr[i], duration: e.target.value }; update({ projects: arr }); }} />
                        <Input className="col-span-2" placeholder="Link (optional)" value={p.link} onChange={(e) => { const arr = [...data.projects]; arr[i] = { ...arr[i], link: e.target.value }; update({ projects: arr }); }} />
                        <Textarea className="col-span-2" rows={2} placeholder="Short description" value={p.description} onChange={(e) => { const arr = [...data.projects]; arr[i] = { ...arr[i], description: e.target.value }; update({ projects: arr }); }} />
                      </div>
                      <div className="flex justify-end mt-2">
                        <Button size="sm" variant="ghost" className="text-destructive" onClick={() => update({ projects: data.projects.filter((_, j) => j !== i) })}>Remove</Button>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>

              <Card className="border-border">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="display font-bold">Awards & Recognition</div>
                    <Button size="sm" variant="outline" className="gap-1" onClick={() => update({ awards: [...(data.awards || []), { title: "", issuer: "", year: "", description: "" }] })} data-testid="award-add"><Plus className="size-3.5" /> Add award</Button>
                  </div>
                  {(data.awards || []).map((a, i) => (
                    <div key={i} className="flex gap-2 mb-2" data-testid={`award-item-${i}`}>
                      <Input className="flex-[4]" placeholder="Title" value={a.title} onChange={(e) => { const arr = [...data.awards]; arr[i] = { ...arr[i], title: e.target.value }; update({ awards: arr }); }} />
                      <Input className="flex-[3]" placeholder="Issuer" value={a.issuer} onChange={(e) => { const arr = [...data.awards]; arr[i] = { ...arr[i], issuer: e.target.value }; update({ awards: arr }); }} />
                      <Input className="flex-[2]" placeholder="Year" value={a.year} onChange={(e) => { const arr = [...data.awards]; arr[i] = { ...arr[i], year: e.target.value }; update({ awards: arr }); }} />
                      <Input className="flex-[3]" placeholder="Details" value={a.description} onChange={(e) => { const arr = [...data.awards]; arr[i] = { ...arr[i], description: e.target.value }; update({ awards: arr }); }} />
                      <Button size="icon" variant={a.page_break_after ? "default" : "ghost"} onClick={() => { const arr = [...data.awards]; arr[i] = { ...arr[i], page_break_after: !arr[i].page_break_after }; update({ awards: arr }); }} title="Force page break after this award" data-testid={`awd-break-${i}`}><CornerDownRight className="size-4" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => update({ awards: data.awards.filter((_, j) => j !== i) })}><Trash2 className="size-4 text-destructive" /></Button>
                    </div>
                  ))}
                </CardContent>
              </Card>

              <Card className="border-border">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="display font-bold">Publications</div>
                    <Button size="sm" variant="outline" className="gap-1" onClick={() => update({ publications: [...(data.publications || []), { title: "", venue: "", year: "", link: "", description: "" }] })} data-testid="pub-add"><Plus className="size-3.5" /> Add publication</Button>
                  </div>
                  {(data.publications || []).map((p, i) => (
                    <div key={i} className="border border-border rounded-md p-3 mb-2" data-testid={`pub-item-${i}`}>
                      <div className="grid grid-cols-12 gap-2">
                        <Input className="col-span-6" placeholder="Title" value={p.title} onChange={(e) => { const arr = [...data.publications]; arr[i] = { ...arr[i], title: e.target.value }; update({ publications: arr }); }} />
                        <Input className="col-span-4" placeholder="Venue (journal/conference)" value={p.venue} onChange={(e) => { const arr = [...data.publications]; arr[i] = { ...arr[i], venue: e.target.value }; update({ publications: arr }); }} />
                        <Input className="col-span-2" placeholder="Year" value={p.year} onChange={(e) => { const arr = [...data.publications]; arr[i] = { ...arr[i], year: e.target.value }; update({ publications: arr }); }} />
                        <Input className="col-span-12" placeholder="Link (optional)" value={p.link} onChange={(e) => { const arr = [...data.publications]; arr[i] = { ...arr[i], link: e.target.value }; update({ publications: arr }); }} />
                      </div>
                      <div className="flex justify-end mt-1 gap-2">
                        <Button size="sm" variant={p.page_break_after ? "default" : "outline"} onClick={() => { const arr = [...data.publications]; arr[i] = { ...arr[i], page_break_after: !arr[i].page_break_after }; update({ publications: arr }); }} className="gap-1" data-testid={`pub-break-${i}`}><CornerDownRight className="size-3.5" /> {p.page_break_after ? "Breaks" : "Break"}</Button>
                        <Button size="sm" variant="ghost" className="text-destructive" onClick={() => update({ publications: data.publications.filter((_, j) => j !== i) })}>Remove</Button>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>

              <Card className="border-border">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="display font-bold">Volunteer Experience</div>
                    <Button size="sm" variant="outline" className="gap-1" onClick={() => update({ volunteer: [...(data.volunteer || []), { role: "", organization: "", duration: "", description: "" }] })} data-testid="vol-add"><Plus className="size-3.5" /> Add</Button>
                  </div>
                  {(data.volunteer || []).map((v2, i) => (
                    <div key={i} className="flex gap-2 mb-2" data-testid={`vol-item-${i}`}>
                      <Input className="flex-[3]" placeholder="Role" value={v2.role} onChange={(e) => { const arr = [...data.volunteer]; arr[i] = { ...arr[i], role: e.target.value }; update({ volunteer: arr }); }} />
                      <Input className="flex-[3]" placeholder="Organization" value={v2.organization} onChange={(e) => { const arr = [...data.volunteer]; arr[i] = { ...arr[i], organization: e.target.value }; update({ volunteer: arr }); }} />
                      <Input className="flex-[2]" placeholder="Duration" value={v2.duration} onChange={(e) => { const arr = [...data.volunteer]; arr[i] = { ...arr[i], duration: e.target.value }; update({ volunteer: arr }); }} />
                      <Button size="icon" variant={v2.page_break_after ? "default" : "ghost"} onClick={() => { const arr = [...data.volunteer]; arr[i] = { ...arr[i], page_break_after: !arr[i].page_break_after }; update({ volunteer: arr }); }} title="Force page break after this" data-testid={`vol-break-${i}`}><CornerDownRight className="size-4" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => update({ volunteer: data.volunteer.filter((_, j) => j !== i) })}><Trash2 className="size-4 text-destructive" /></Button>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Sidebar (right column) */}
            <TabsContent value="sidebar" className="mt-5 space-y-4">
              <Card className="border-border">
                <CardContent className="p-4">
                  <SectionHeader title="Core Competencies" onAi={() => aiRewrite("competencies", JSON.stringify(data.competencies), (out) => {
                    try { const arr = JSON.parse(out); if (Array.isArray(arr)) update({ competencies: arr.map(String) }); }
                    catch { update({ competencies: out.split(/[,\n]/).map(s => s.trim()).filter(Boolean) }); }
                  })} aiBusy={aiBusy} testidPrefix="competencies" />
                  <Textarea rows={3} placeholder="Comma-separated tags" value={(data.competencies || []).join(", ")} onChange={(e) => update({ competencies: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })} data-testid="field-competencies" />
                </CardContent>
              </Card>

              <Card className="border-border">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="display font-bold">Certifications</div>
                    <Button size="sm" variant="outline" onClick={() => update({ certifications: [...data.certifications, { name: "", issuer: "", year: "" }] })} className="gap-1" data-testid="cert-add"><Plus className="size-3.5" /> Add</Button>
                  </div>
                  {data.certifications.map((c, i) => (
                    <div key={i} className="flex gap-2 mb-2">
                      <Input className="flex-[4]" placeholder="Name" value={c.name} onChange={(e) => updCert(i, { name: e.target.value })} />
                      <Input className="flex-[3]" placeholder="Issuer" value={c.issuer} onChange={(e) => updCert(i, { issuer: e.target.value })} />
                      <Input className="flex-[2]" placeholder="Year" value={c.year} onChange={(e) => updCert(i, { year: e.target.value })} />
                      <Button size="icon" variant={c.page_break_after ? "default" : "ghost"} onClick={() => updCert(i, { page_break_after: !c.page_break_after })} title="Force page break after this cert" data-testid={`cert-break-${i}`}><CornerDownRight className="size-4" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => update({ certifications: data.certifications.filter((_, j) => j !== i) })}><Trash2 className="size-4 text-destructive" /></Button>
                    </div>
                  ))}
                </CardContent>
              </Card>

              <Card className="border-border">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="display font-bold">Languages</div>
                    <Button size="sm" variant="outline" onClick={() => update({ languages: [...data.languages, { name: "", proficiency: "Professional" }] })} className="gap-1" data-testid="lang-add"><Plus className="size-3.5" /> Add</Button>
                  </div>
                  {data.languages.map((l, i) => (
                    <div key={i} className="flex gap-2 mb-2">
                      <Input className="flex-[4]" placeholder="Language" value={l.name} onChange={(e) => updLang(i, { name: e.target.value })} />
                      <Select value={l.proficiency} onValueChange={(v) => updLang(i, { proficiency: v })}>
                        <SelectTrigger className="flex-[3]"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {["Native","Fluent","Professional","Conversational","Basic"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <Button size="icon" variant={l.page_break_after ? "default" : "ghost"} onClick={() => updLang(i, { page_break_after: !l.page_break_after })} title="Force page break after this language" data-testid={`lang-break-${i}`}><CornerDownRight className="size-4" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => update({ languages: data.languages.filter((_, j) => j !== i) })}><Trash2 className="size-4 text-destructive" /></Button>
                    </div>
                  ))}
                </CardContent>
              </Card>

              <Card className="border-border">
                <CardContent className="p-4">
                  <SectionHeader title="Key Highlights" onAi={() => aiRewrite("highlights", JSON.stringify(data.highlights), (out) => {
                    try { const arr = JSON.parse(out); if (Array.isArray(arr)) update({ highlights: arr.map(h => ({ title: h.title || "", description: h.description || "" })) }); } catch {}
                  })} aiBusy={aiBusy} testidPrefix="highlights" />
                  <Button size="sm" variant="outline" onClick={() => update({ highlights: [...data.highlights, { title: "", description: "" }] })} className="gap-1 mb-2" data-testid="high-add"><Plus className="size-3.5" /> Add</Button>
                  {data.highlights.map((h, i) => (
                    <div key={i} className="border border-border rounded-md p-2 mb-2 grid gap-2">
                      <Input placeholder="Title" value={h.title} onChange={(e) => updHigh(i, { title: e.target.value })} />
                      <Textarea rows={2} placeholder="Description" value={h.description} onChange={(e) => updHigh(i, { description: e.target.value })} />
                      <div className="flex justify-end gap-2">
                        <Button size="sm" variant={h.page_break_after ? "default" : "outline"} onClick={() => updHigh(i, { page_break_after: !h.page_break_after })} className="gap-1" data-testid={`hl-break-${i}`}><CornerDownRight className="size-3.5" /> {h.page_break_after ? "Breaks" : "Break"}</Button>
                        <Button size="sm" variant="ghost" className="text-destructive" onClick={() => update({ highlights: data.highlights.filter((_, j) => j !== i) })}>Remove</Button>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>

              <Card className="border-border">
                <CardContent className="p-4">
                  <Label className="text-xs font-semibold">Additional info (one per line)</Label>
                  <Textarea rows={3} value={(data.additional_info || []).join("\n")} onChange={(e) => update({ additional_info: e.target.value.split("\n").map(s => s.trim()).filter(Boolean) })} data-testid="field-additional" />
                </CardContent>
              </Card>
            </TabsContent>

            {/* Custom sections */}
            <TabsContent value="custom" className="mt-5 space-y-4">
              <CustomSectionsEditor data={data} update={update} />
            </TabsContent>

            {/* Page breaks — per-section toggles (sidebar + main) */}
            <TabsContent value="breaks" className="mt-5 space-y-4">
              <LayoutEditor data={data} update={update} metrics={metrics} template={currentTemplate} onAiFit={onAiFitToPages} aiBusy={aiBusy} />
              <PageBreaksEditor data={data} update={update} />
              <SignatureEditor data={data} update={update} resumeId={id} />
            </TabsContent>
          </Tabs>
        </div>

        {/* Right: live preview (pixel-true to PDF) */}
        <div className="overflow-y-auto bg-secondary/30 grid-bg p-5 md:p-7">
          <div className="max-w-[640px] mx-auto">
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-2 flex items-center gap-2 flex-wrap">
              <Sparkles className="size-3.5" />
              <span>Live preview · {currentTemplate?.page_size || "A4"} · {currentTemplate?.density || "normal"} · {Math.round(Number(currentTemplate?.base_pt || 10))}pt · {Math.round(Number(currentTemplate?.page_margin_mm ?? 12))}mm margin</span>
              {(() => {
                const target = Number(currentTemplate?.target_pages || 0);
                if (target > 0 && metrics.pages === target) {
                  return <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-600 text-white font-bold normal-case tracking-normal text-[11px]" data-testid="preview-target-hit-badge">
                    <FileCheck2 className="size-3.5" /> {target} PAGE{target > 1 ? "S" : ""} · TARGET HIT ✓
                  </span>;
                }
                if (target > 0 && metrics.pages > target) {
                  return <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-500 text-white font-bold normal-case tracking-normal text-[11px]" data-testid="preview-target-over-badge">
                    <AlertTriangle className="size-3.5" /> PAGE {metrics.pages} OF {target} · {Math.round(metrics.heightMm - target * metrics.pageHeightMm)}mm OVER
                  </span>;
                }
                if (target > 0 && metrics.pages < target) {
                  return <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-sky-500 text-white font-bold normal-case tracking-normal text-[11px]" data-testid="preview-target-under-badge">
                    <AlertTriangle className="size-3.5" /> PAGE {metrics.pages} OF {target} · UNDER
                  </span>;
                }
                if (metrics.pages === 1) {
                  return <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 font-bold normal-case tracking-normal text-[11px]" data-testid="preview-fits-badge">
                    <FileCheck2 className="size-3.5" /> PAGE 1 · FITS ✓
                  </span>;
                }
                return <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-500/15 text-amber-700 dark:text-amber-300 font-bold normal-case tracking-normal text-[11px]" data-testid="preview-overflow-badge">
                  <AlertTriangle className="size-3.5" /> SPANS {metrics.pages} PAGES · +{Math.max(0, Math.round(metrics.heightMm - metrics.pageHeightMm))}mm
                </span>;
              })()}
              <button onClick={openFullPreview} className="ml-auto inline-flex items-center gap-1 text-primary hover:underline normal-case tracking-normal" data-testid="preview-fullpreview-link">
                <ExternalLink className="size-3" /> Open full preview
              </button>
            </div>
            <div className="text-[10px] text-muted-foreground mb-2 normal-case tracking-normal flex items-center gap-1.5" data-testid="preview-edit-hint">
              <Sparkles className="size-3" /> Word-like inline editing: click any name, title, summary or bullet directly in the preview to edit. Press <kbd className="px-1.5 py-0.5 rounded bg-secondary text-foreground border border-border text-[9px]">Enter</kbd> or <kbd className="px-1.5 py-0.5 rounded bg-secondary text-foreground border border-border text-[9px]">Esc</kbd> to commit.
            </div>
            <PreviewCanvas data={data} style={currentTemplate} onMetrics={setMetrics} editable onTextEdit={onInlineTextEdit} />
          </div>
        </div>
      </div>

      {/* AI Assistant Drawer */}
      <AIAssistantDrawer open={chatOpen} onClose={() => setChatOpen(false)} resumeId={id} currentData={data} onPatch={onChatPatch} />

      {/* AI Auto-Fit dialog */}
      <AIAutoFitDialog open={autofitOpen} onOpenChange={setAutofitOpen} resumeId={id} currentData={data} onApply={onAutofitApply} />
      <SkillGapDrawer open={skillGapOpen} onOpenChange={setSkillGapOpen} resumeId={id} />

      {/* Job Profile Score drawer */}
      <JobScoreDrawer open={scoreOpen} onOpenChange={setScoreOpen} resumeId={id} onDataPatched={(d) => setData(d)} />

      {/* Send Email dialog */}
      <SendEmailDialog open={emailOpen} onOpenChange={setEmailOpen} resume={resume} />
    </div>
  );

  function updateExp(i, patch) { const arr = [...data.experiences]; arr[i] = { ...arr[i], ...patch }; update({ experiences: arr }); }
  function moveExp(i, dir) {
    const arr = [...data.experiences];
    const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    update({ experiences: arr });
  }
  function moveItem(key, i, dir) {
    const arr = [...(data[key] || [])];
    const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    update({ [key]: arr });
  }
  // Generic drag-and-drop reorder for any array section (experiences,
  // education, projects, etc.). Returns props to spread on the draggable
  // wrapper. Uses native HTML5 DnD — no extra dependency.
  function reorderProps(key, idx) {
    const arr = data[key] || [];
    return {
      draggable: true,
      onDragStart: (ev) => { ev.dataTransfer.effectAllowed = "move"; ev.dataTransfer.setData("text/plain", `${key}:${idx}`); },
      onDragOver: (ev) => { ev.preventDefault(); ev.dataTransfer.dropEffect = "move"; ev.currentTarget.classList.add("ring-2", "ring-primary/60"); },
      onDragLeave: (ev) => { ev.currentTarget.classList.remove("ring-2", "ring-primary/60"); },
      onDrop: (ev) => {
        ev.preventDefault();
        ev.currentTarget.classList.remove("ring-2", "ring-primary/60");
        const [k, fromStr] = (ev.dataTransfer.getData("text/plain") || "").split(":");
        if (k !== key) return;
        const from = Number(fromStr);
        if (from === idx || Number.isNaN(from)) return;
        const next = [...arr];
        const [moved] = next.splice(from, 1);
        next.splice(idx, 0, moved);
        update({ [key]: next });
      },
    };
  }
  function updEdu(i, patch) { const arr = [...data.education]; arr[i] = { ...arr[i], ...patch }; update({ education: arr }); }
  function updCert(i, patch) { const arr = [...data.certifications]; arr[i] = { ...arr[i], ...patch }; update({ certifications: arr }); }
  function updLang(i, patch) { const arr = [...data.languages]; arr[i] = { ...arr[i], ...patch }; update({ languages: arr }); }
  function updHigh(i, patch) { const arr = [...data.highlights]; arr[i] = { ...arr[i], ...patch }; update({ highlights: arr }); }
}

function SaveBadge({ state }) {
  if (state === "saving") return <span className="inline-flex items-center gap-1 text-[10px] text-primary normal-case tracking-normal"><Loader2 className="size-3 animate-spin" /> Saving…</span>;
  if (state === "saved") return <span className="inline-flex items-center gap-1 text-[10px] text-emerald-600 dark:text-emerald-400 normal-case tracking-normal"><Check className="size-3" /> Saved</span>;
  if (state === "dirty") return <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground normal-case tracking-normal">Unsaved…</span>;
  return null;
}

function Field({ label, children, testid }) {
  return (
    <div data-testid={testid}>
      <Label className="text-xs font-semibold">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function SectionHeader({ title, onAi, aiBusy, testidPrefix }) {
  return (
    <div className="flex items-center justify-between mb-2">
      <div className="display font-bold">{title}</div>
      <Button size="sm" variant="outline" onClick={onAi} disabled={aiBusy} className="gap-1" data-testid={`${testidPrefix}-ai-button`}>
        {aiBusy ? <Loader2 className="size-3.5 animate-spin" /> : <Wand2 className="size-3.5" />} AI rewrite
      </Button>
    </div>
  );
}


function CustomSectionsEditor({ data, update }) {
  const list = data.custom_sections || [];
  const setList = (next) => update({ custom_sections: next });

  const addSection = (type) => {
    const s = {
      id: crypto.randomUUID ? crypto.randomUUID() : `cs-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      title: type === "text" ? "About me" : type === "bullets" ? "Hobbies" : type === "timeline" ? "Milestones" : "Key Facts",
      type,
      column: "left",
      visible: true,
      page_break_after: false,
      content_text: "",
      items: type === "text" ? [] : [{ title: "", subtitle: "", date: "", description: "", text: "" }],
    };
    setList([...list, s]);
    toast.success(`Added ${type} section`);
  };

  const updateSec = (i, patch) => {
    const arr = [...list]; arr[i] = { ...arr[i], ...patch }; setList(arr);
  };
  const removeSec = (i) => {
    if (!window.confirm("Remove this custom section?")) return;
    setList(list.filter((_, j) => j !== i));
  };
  const moveSec = (i, dir) => {
    const j = i + dir; if (j < 0 || j >= list.length) return;
    const arr = [...list]; [arr[i], arr[j]] = [arr[j], arr[i]]; setList(arr);
  };
  const updateItem = (i, k, patch) => {
    const arr = [...list];
    const items = [...(arr[i].items || [])];
    items[k] = { ...items[k], ...patch };
    arr[i] = { ...arr[i], items };
    setList(arr);
  };
  const addItem = (i) => {
    const arr = [...list];
    const items = [...(arr[i].items || []), { title: "", subtitle: "", date: "", description: "", text: "" }];
    arr[i] = { ...arr[i], items };
    setList(arr);
  };
  const removeItem = (i, k) => {
    const arr = [...list];
    arr[i] = { ...arr[i], items: arr[i].items.filter((_, j) => j !== k) };
    setList(arr);
  };

  return (
    <>
      <Card className="border-border">
        <CardContent className="p-4">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div>
              <div className="display font-bold text-lg">Custom sections</div>
              <div className="text-xs text-muted-foreground">Add arbitrary sections (text, bullets, timeline, cards) and place them in the left or right column.</div>
            </div>
            <div className="flex items-center gap-1.5 flex-wrap">
              <Button size="sm" variant="outline" onClick={() => addSection("text")} className="gap-1 lift" data-testid="cs-add-text"><Plus className="size-3.5" /> Text</Button>
              <Button size="sm" variant="outline" onClick={() => addSection("bullets")} className="gap-1 lift" data-testid="cs-add-bullets"><Plus className="size-3.5" /> Bullets</Button>
              <Button size="sm" variant="outline" onClick={() => addSection("timeline")} className="gap-1 lift" data-testid="cs-add-timeline"><Plus className="size-3.5" /> Timeline</Button>
              <Button size="sm" variant="outline" onClick={() => addSection("cards")} className="gap-1 lift" data-testid="cs-add-cards"><Plus className="size-3.5" /> Cards</Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {list.length === 0 && (
        <Card className="border-dashed border-border">
          <CardContent className="p-8 text-center text-sm text-muted-foreground">
            No custom sections yet. Use the buttons above to add one.
          </CardContent>
        </Card>
      )}

      {list.map((cs, i) => (
        <Card key={cs.id} className="border-border" data-testid={`cs-card-${i}`}>
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-2 flex-wrap">
              <Input value={cs.title || ""} onChange={(e) => updateSec(i, { title: e.target.value })} className="max-w-[260px] font-bold" placeholder="Section title" data-testid={`cs-title-${i}`} />
              <span className="chip bg-secondary text-foreground">{cs.type}</span>
              <Select value={cs.column} onValueChange={(v) => updateSec(i, { column: v })}>
                <SelectTrigger className="w-[130px] h-9" data-testid={`cs-column-${i}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="left">Left column</SelectItem>
                  <SelectItem value="right">Right (sidebar)</SelectItem>
                </SelectContent>
              </Select>
              <div className="ml-auto flex items-center gap-1">
                <Button size="icon" variant="ghost" disabled={i === 0} onClick={() => moveSec(i, -1)} data-testid={`cs-up-${i}`}><ArrowUp className="size-4" /></Button>
                <Button size="icon" variant="ghost" disabled={i === list.length - 1} onClick={() => moveSec(i, 1)} data-testid={`cs-down-${i}`}><ArrowDown className="size-4" /></Button>
                <Button size="sm" variant={cs.page_break_after ? "default" : "outline"} onClick={() => updateSec(i, { page_break_after: !cs.page_break_after })} className="gap-1 h-8 px-2" data-testid={`cs-break-${i}`}>
                  <CornerDownRight className="size-3.5" /> Break
                </Button>
                <Button size="icon" variant="ghost" onClick={() => removeSec(i)} className="text-destructive" data-testid={`cs-del-${i}`}><Trash2 className="size-4" /></Button>
              </div>
            </div>

            {cs.type === "text" && (
              <Textarea rows={4} value={cs.content_text || ""} onChange={(e) => updateSec(i, { content_text: e.target.value })} placeholder="Write your section content…" data-testid={`cs-text-${i}`} />
            )}

            {cs.type === "bullets" && (
              <div className="space-y-1.5">
                {(cs.items || []).map((it, k) => (
                  <div key={k} className="flex gap-2">
                    <Input value={it.text || ""} onChange={(e) => updateItem(i, k, { text: e.target.value })} placeholder="Bullet text" data-testid={`cs-bullet-${i}-${k}`} />
                    <Button size="icon" variant="ghost" onClick={() => removeItem(i, k)}><Trash2 className="size-4 text-destructive" /></Button>
                  </div>
                ))}
                <Button size="sm" variant="outline" onClick={() => addItem(i)} className="gap-1"><Plus className="size-3.5" /> Add bullet</Button>
              </div>
            )}

            {cs.type === "timeline" && (
              <div className="space-y-2">
                {(cs.items || []).map((it, k) => (
                  <div key={k} className="border border-border rounded-md p-2 space-y-1.5">
                    <div className="grid grid-cols-2 gap-2">
                      <Input value={it.title || ""} onChange={(e) => updateItem(i, k, { title: e.target.value })} placeholder="Title" data-testid={`cs-t-title-${i}-${k}`} />
                      <Input value={it.date || ""} onChange={(e) => updateItem(i, k, { date: e.target.value })} placeholder="Date / duration" />
                    </div>
                    <Input value={it.subtitle || ""} onChange={(e) => updateItem(i, k, { subtitle: e.target.value })} placeholder="Subtitle (optional)" />
                    <Textarea rows={2} value={it.description || ""} onChange={(e) => updateItem(i, k, { description: e.target.value })} placeholder="Description" />
                    <div className="flex justify-end"><Button size="sm" variant="ghost" className="text-destructive" onClick={() => removeItem(i, k)}>Remove</Button></div>
                  </div>
                ))}
                <Button size="sm" variant="outline" onClick={() => addItem(i)} className="gap-1"><Plus className="size-3.5" /> Add entry</Button>
              </div>
            )}

            {cs.type === "cards" && (
              <div className="space-y-2">
                {(cs.items || []).map((it, k) => (
                  <div key={k} className="border border-border rounded-md p-2 space-y-1.5">
                    <Input value={it.title || ""} onChange={(e) => updateItem(i, k, { title: e.target.value })} placeholder="Card title" data-testid={`cs-c-title-${i}-${k}`} />
                    <Textarea rows={2} value={it.description || ""} onChange={(e) => updateItem(i, k, { description: e.target.value })} placeholder="Description" />
                    <div className="flex justify-end"><Button size="sm" variant="ghost" className="text-destructive" onClick={() => removeItem(i, k)}>Remove</Button></div>
                  </div>
                ))}
                <Button size="sm" variant="outline" onClick={() => addItem(i)} className="gap-1"><Plus className="size-3.5" /> Add card</Button>
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </>
  );
}

function PageBreaksEditor({ data, update }) {
  const breaks = data.section_breaks || {};
  const setBreak = (key, v) => update({ section_breaks: { ...breaks, [key]: v } });

  const leftSections = [
    { key: "summary", label: "Professional Summary", count: data.summary ? 1 : 0 },
    { key: "experiences", label: "Professional Experience", count: (data.experiences || []).length },
    { key: "earlier_roles", label: "Earlier Roles", count: (data.earlier_roles || []).length },
    { key: "education", label: "Education", count: (data.education || []).length },
    { key: "projects", label: "Projects", count: (data.projects || []).length },
    { key: "awards", label: "Awards", count: (data.awards || []).length },
    { key: "publications", label: "Publications", count: (data.publications || []).length },
    { key: "volunteer", label: "Volunteer", count: (data.volunteer || []).length },
  ];
  const rightSections = [
    { key: "competencies", label: "Core Competencies", count: (data.competencies || []).length },
    { key: "certifications", label: "Certifications", count: (data.certifications || []).length },
    { key: "languages", label: "Languages", count: (data.languages || []).length },
    { key: "highlights", label: "Key Highlights", count: (data.highlights || []).length },
    { key: "additional_info", label: "Additional Info", count: (data.additional_info || []).length },
  ];

  const totalForced = Object.values(breaks).filter(Boolean).length +
    (data.experiences || []).filter((x) => x.page_break_after).length +
    (data.earlier_roles || []).filter((x) => x.page_break_after).length +
    (data.education || []).filter((x) => x.page_break_after).length +
    (data.projects || []).filter((x) => x.page_break_after).length +
    (data.awards || []).filter((x) => x.page_break_after).length +
    (data.publications || []).filter((x) => x.page_break_after).length +
    (data.volunteer || []).filter((x) => x.page_break_after).length +
    (data.certifications || []).filter((x) => x.page_break_after).length +
    (data.languages || []).filter((x) => x.page_break_after).length +
    (data.highlights || []).filter((x) => x.page_break_after).length +
    (data.custom_sections || []).filter((x) => x.page_break_after).length;

  return (
    <>
      <Card className="border-border">
        <CardContent className="p-4">
          <div className="display font-bold text-lg">Page breaks</div>
          <p className="text-xs text-muted-foreground mt-1">Toggle a section to force a page break AFTER that section ends. For per-item breaks (e.g. break between two jobs), use the "Break" buttons in the Experience / Education / Projects tabs.</p>
          <div className="mt-2 text-xs text-muted-foreground">Total forced breaks: <span className="font-bold text-foreground">{totalForced}</span></div>
        </CardContent>
      </Card>

      <Card className="border-border">
        <CardContent className="p-4">
          <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-muted-foreground mb-2">Main column sections</div>
          <div className="space-y-2">
            {leftSections.map((sec) => (
              <div key={sec.key} className="flex items-center justify-between p-2.5 rounded-md bg-secondary/40 border border-border" data-testid={`break-row-${sec.key}`}>
                <div className="min-w-0">
                  <div className="font-semibold text-sm">{sec.label}</div>
                  <div className="text-[11px] text-muted-foreground">{sec.count} item{sec.count === 1 ? "" : "s"}</div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[11px] text-muted-foreground">Break after</span>
                  <Switch checked={!!breaks[sec.key]} onCheckedChange={(v) => setBreak(sec.key, v)} data-testid={`break-toggle-${sec.key}`} disabled={sec.count === 0} />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="border-border">
        <CardContent className="p-4">
          <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-muted-foreground mb-2">Sidebar sections</div>
          <div className="space-y-2">
            {rightSections.map((sec) => (
              <div key={sec.key} className="flex items-center justify-between p-2.5 rounded-md bg-secondary/40 border border-border" data-testid={`break-row-${sec.key}`}>
                <div className="min-w-0">
                  <div className="font-semibold text-sm">{sec.label}</div>
                  <div className="text-[11px] text-muted-foreground">{sec.count} item{sec.count === 1 ? "" : "s"}</div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[11px] text-muted-foreground">Break after</span>
                  <Switch checked={!!breaks[sec.key]} onCheckedChange={(v) => setBreak(sec.key, v)} data-testid={`break-toggle-${sec.key}`} disabled={sec.count === 0} />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </>
  );
}


function LayoutEditor({ data, update, metrics, template, onAiFit, aiBusy }) {
  const m = data.page_margin_mm_override;
  const setMargin = (v) => update({ page_margin_mm_override: v });

  // Smart "Fit to N pages" — computes the EXACT margin that will make the
  // current measured content fit into N pages of the CURRENT paper size.
  // Math:
  //   contentMm   = actual rendered content height (margins stripped)
  //   pageH       = current paper height (A4 297mm, Letter 279.4mm, Legal 355.6mm, or custom)
  //   targetH(m)  = N * (pageH - 2m)
  //   require:  contentMm <= targetH(m)
  //   solve  :  m <= (N * pageH - contentMm) / (2 * N)
  // We clamp to [3, 25] mm. If the required margin is below 3mm, we surface a
  // warning that AI Auto-Fit is needed (margin alone cannot squeeze further).
  const fitTo = (pages) => {
    const pageH = Number(metrics?.pageHeightMm) || 297;
    const curMargin = Number(metrics?.marginMm ?? (template?.page_margin_mm ?? 12));
    // contentMm (heightMm already INCLUDES 2*margin per our PreviewCanvas math)
    const totalMm = Number(metrics?.heightMm) || pageH;
    const contentMm = Math.max(0, totalMm - 2 * curMargin);
    const idealMargin = (pages * pageH - contentMm) / (2 * pages);
    let target = Math.round(idealMargin);
    if (!Number.isFinite(target)) target = 12;
    if (target > 25) target = 22;            // Cap upper
    if (target < 3) {
      setMargin(3);
      toast.warning(`Margin reduced to 3mm — content is still ${Math.round(contentMm - pages * (pageH - 6))}mm over for ${pages} page${pages > 1 ? "s" : ""}. Use AI Auto-Fit to trim text.`);
      return;
    }
    setMargin(target);
    const willFit = contentMm <= pages * (pageH - 2 * target);
    toast.success(`Margin set to ${target}mm for ${pages} page${pages > 1 ? "s" : ""}. ${willFit ? "Content fits ✓" : "Slight overflow — try AI Auto-Fit."}`);
  };

  const paperLabel = template?.page_size === "Custom"
    ? `Custom ${Math.round(template?.custom_width_mm || 210)}×${Math.round(template?.custom_height_mm || 297)}mm`
    : template?.page_size || "A4";

  return (
    <Card className="border-border">
      <CardContent className="p-4 space-y-3">
        <div>
          <div className="display font-bold text-lg">Page layout</div>
          <p className="text-xs text-muted-foreground mt-0.5">Paper: <strong>{paperLabel}</strong> · Current: <strong>{metrics?.pages || 1} page{(metrics?.pages || 1) > 1 ? "s" : ""}</strong> · Override the margin just for this resume.</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <label className="text-sm font-semibold">Page margin</label>
          <div className="flex items-center gap-1" data-testid="layout-margin-presets">
            {[{ label: "Auto", v: null }, { label: "Compact · 8mm", v: 8 }, { label: "Normal · 12mm", v: 12 }, { label: "Comfortable · 16mm", v: 16 }, { label: "Wide · 22mm", v: 22 }].map((p) => {
              const active = m === p.v || (p.v === null && m == null);
              return (
                <Button key={p.label} size="sm" variant={active ? "default" : "outline"} onClick={() => setMargin(p.v)} data-testid={`layout-margin-${p.v ?? "auto"}`}>
                  {p.label}
                </Button>
              );
            })}
          </div>
          <div className="flex items-center gap-2 ml-2">
            <span className="text-xs text-muted-foreground">Custom</span>
            <Input type="number" min={0} max={40} step={1} value={m == null ? "" : m} onChange={(e) => setMargin(e.target.value === "" ? null : Number(e.target.value))} className="w-20 h-8" data-testid="layout-margin-custom" />
            <span className="text-xs text-muted-foreground">mm</span>
          </div>
        </div>
        <div className="border-t border-border pt-3">
          <div className="text-sm font-semibold mb-2">Fit to N pages <span className="ml-1 text-[10px] font-normal text-muted-foreground">(computed from {paperLabel} + measured content)</span></div>
          <div className="flex items-center gap-2 flex-wrap">
            {[1, 2, 3].map((n) => (
              <Button key={n} size="sm" variant="outline" onClick={() => fitTo(n)} className="gap-1" data-testid={`fit-to-${n}`}>
                <Target className="size-3.5" /> Fit to {n} page{n > 1 ? "s" : ""}
              </Button>
            ))}
            <span className="text-[11px] text-muted-foreground">If margin reaches its floor, run <strong>AI Auto-Fit</strong> to trim/expand text.</span>
          </div>
        </div>
        <div className="border-t border-border pt-3">
          <div className="text-sm font-semibold mb-2 flex items-center gap-1.5"><Wand2 className="size-3.5 text-primary" /> AI Auto-Fit to N pages <span className="text-[10px] font-normal text-muted-foreground">(rewrites + tightens margin in one click)</span></div>
          <div className="flex items-center gap-2 flex-wrap">
            {[1, 2, 3].map((n) => (
              <Button key={`ai-${n}`} size="sm" variant="outline" disabled={aiBusy} onClick={() => onAiFit?.(n)} className="gap-1 border-primary/40 hover:bg-primary/10" data-testid={`ai-fit-to-${n}`}>
                {aiBusy ? <Loader2 className="size-3.5 animate-spin" /> : <Sparkles className="size-3.5 text-primary" />}
                AI · Fit to {n} page{n > 1 ? "s" : ""}
              </Button>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function SignatureEditor({ data, update, resumeId }) {
  const [uploading, setUploading] = useState(false);
  const ref = useRef(null);
  const onPick = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const r = await api.post(`/resumes/${resumeId}/upload-signature`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      update({ signature_url: r.data.url });
      toast.success("Signature uploaded");
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); if (ref.current) ref.current.value = ""; }
  };
  return (
    <Card className="border-border">
      <CardContent className="p-4 space-y-3">
        <div>
          <div className="display font-bold text-lg">Signature (optional)</div>
          <p className="text-xs text-muted-foreground mt-0.5">Adds a small signature image to the bottom of the last page. Supports PNG / JPG. Keep the image small (recommend under 300×100 px).</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <input type="file" accept=".png,.jpg,.jpeg,.webp" ref={ref} onChange={onPick} className="hidden" data-testid="signature-file" />
          {data.signature_url ? (
            <>
              <img src={data.signature_url} alt="sig" className="max-h-12 border border-border rounded bg-white px-2" data-testid="signature-preview" />
              <Button size="sm" variant="outline" onClick={() => ref.current?.click()} disabled={uploading} data-testid="signature-replace">{uploading ? "Uploading…" : "Replace"}</Button>
              <Button size="sm" variant="ghost" className="text-destructive" onClick={() => update({ signature_url: "" })} data-testid="signature-remove">Remove</Button>
            </>
          ) : (
            <Button size="sm" variant="outline" onClick={() => ref.current?.click()} disabled={uploading} className="gap-2" data-testid="signature-upload">{uploading ? <Loader2 className="size-3.5 animate-spin" /> : <Plus className="size-3.5" />} Upload signature</Button>
          )}
          <div className="ml-auto flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Position</span>
            <Select value={data.signature_position || "right"} onValueChange={(v) => update({ signature_position: v })}>
              <SelectTrigger className="w-[120px] h-9" data-testid="signature-position"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="right">Bottom right</SelectItem><SelectItem value="left">Bottom left</SelectItem></SelectContent>
            </Select>
          </div>
        </div>
        <Input placeholder="Label (optional) — e.g. 'Authorized signatory' or your name" value={data.signature_label || ""} onChange={(e) => update({ signature_label: e.target.value })} data-testid="signature-label" />
      </CardContent>
    </Card>
  );
}

